import axios from 'axios';
// LAP-733 (GE022) round 10 — read the user's active UI language from i18next
// so every SmartFeed API call carries it (Accept-Language header + ?language
// query). This guarantees that ChatGPT-driven publications are generated in
// the user's language and that translated content in define_publication /
// user_feed_publication is served correctly.
import i18n, { SUPPORTED_LANGUAGES } from '../../i18n';

const API_BASE_URL = process.env.REACT_APP_LIFE_BACKEND_URL || 'http://localhost:8000';

const getAuthToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

// LAP-733 (GE022) round 10 — resolve the primary tag of the active i18next
// language (e.g. 'fr-FR' → 'fr') and fall back to 'fr' so the backend always
// gets a value it understands.
const getActiveLanguage = () => {
  const raw = (i18n && i18n.language) || '';
  const primary = String(raw).split('-')[0].split('_')[0].toLowerCase();
  return SUPPORTED_LANGUAGES && SUPPORTED_LANGUAGES.includes(primary) ? primary : 'fr';
};

const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/smartfeed`,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    // LAP-733 (GE022) round 10 — always advertise the user's UI language.
    try {
      const lang = getActiveLanguage();
      config.headers = config.headers || {};
      if (!config.headers['Accept-Language'] && !config.headers['accept-language']) {
        config.headers['Accept-Language'] = lang;
      }
      // Also append ?language=xx unless the caller already provided one (the
      // SmartFeed helpers below pass an explicit language sometimes).
      if (config.url && !/[?&]language=/.test(config.url)) {
        const sep = config.url.includes('?') ? '&' : '?';
        config.url = `${config.url}${sep}language=${lang}`;
      }
      // For POST/PATCH payloads (e.g. generateFeed) make sure a "language"
      // field is present so the backend ChatGPT prompt builder picks it up
      // even if it only inspects the body.
      if (config.data && typeof config.data === 'object' && !Array.isArray(config.data)
          && config.data.language == null) {
        config.data = { ...config.data, language: lang };
      }
    } catch (_) {
      // Never block a request because of language injection.
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const smartFeedAPI = {
  getFeed: async (limit = 20, offset = 0, domain = null, type = null, subscription = null, language = null, status = 'active') => {
    try {
      const params = { limit, offset };
      if (domain) params.domain = domain;
      if (type) params.type = type;
      if (subscription) params.subscription = subscription;
      if (language) params.language = language;
      if (status) params.status = status;
      
      const response = await axiosInstance.get('/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching feed:', error);
      // Handle subscription-based access errors
      if (error.response?.status === 403) {
        throw new Error('SUBSCRIPTION_REQUIRED');
      }
      throw error;
    }
  },

  generateFeed: async (count = 10, subscription = null, language = null) => {
    try {
      const payload = { count };
      if (subscription) payload.subscription = subscription;
      if (language) payload.language = language;
      
      const response = await axiosInstance.post('/', payload);
      return response.data;
    } catch (error) {
      console.error('Error generating feed:', error);
      if (error.response?.status === 403) {
        throw new Error('SUBSCRIPTION_REQUIRED');
      }
      throw error;
    }
  },

  refreshFeed: async (subscription = null, language = null) => {
    try {
      const params = {};
      if (subscription) params.subscription = subscription;
      if (language) params.language = language;
      
      const response = await axiosInstance.get('/refresh/', { params });
      return response.data;
    } catch (error) {
      console.error('Error refreshing feed:', error);
      
      // Handle network errors
      if (!error.response) {
        // Network error (server not reachable, CORS, etc.)
        const networkError = new Error('NETWORK_ERROR');
        networkError.message = error.message || 'Failed to fetch';
        networkError.code = error.code || 'ERR_NETWORK';
        throw networkError;
      }
      
      if (error.response?.status === 403) {
        throw new Error('SUBSCRIPTION_REQUIRED');
      }
      // Re-throw the error to preserve status code (429 for rate limit)
      throw error;
    }
  },

  getSavedPublications: async (limit = 20, offset = 0, subscription = null) => {
    try {
      const params = { limit, offset };
      if (subscription) params.subscription = subscription;
      
      const response = await axiosInstance.get('/saved/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching saved publications:', error);
      if (error.response?.status === 403) {
        throw new Error('SUBSCRIPTION_REQUIRED');
      }
      throw error;
    }
  },

  interact: async (publicationId, action) => {
    try {
      const response = await axiosInstance.post(
        `/${publicationId}/interact/`,
        { action }
      );
      return response.data;
    } catch (error) {
      console.error('Error interacting with publication:', error);
      throw error;
    }
  },

  getUserSubscription: async () => {
    try {
      const response = await axiosInstance.get('/subscription/');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Return default if endpoint doesn't exist yet
      return { subscription: 'FREE' };
    }
  },

  getPublicationDefinitions: async (subscription = null) => {
    try {
      const params = {};
      if (subscription) params.subscription = subscription;
      
      const response = await axiosInstance.get('/definitions/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching publication definitions:', error);
      return { definitions: [] };
    }
  },

  updateUserPreferences: async (preferences) => {
    try {
      const response = await axiosInstance.post('/preferences/', preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  },
};
